import { useState, useEffect } from "react";
import { cx } from "@/utils/cx";
import type { NavItemDividerType, NavItemType } from "../config";
import { NavItemBase } from "./nav-item";
import { NavCollapsedMacroPopover } from "./nav-collapsed-macro-popover";
import { isRouteActive } from "@/utils/route-matcher";

interface NavListProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** Additional CSS classes to apply to the list. */
    className?: string;
    /** List of items to display. */
    items: (NavItemType | NavItemDividerType)[];
    /** Whether the sidebar is collapsed. */
    isCollapsed?: boolean;
}

export const NavList = ({ activeUrl, items, className, isCollapsed = false }: NavListProps) => {
    const [open, setOpen] = useState(false);
    const activeItem = items.find((item) => isRouteActive(item.href, activeUrl) || item.items?.some((subItem) => isRouteActive(subItem.href, activeUrl)));
    const [currentItem, setCurrentItem] = useState(activeItem);

    // Sincronizza currentItem quando activeItem cambia (quando cambia activeUrl)
    useEffect(() => {
        if (activeItem) {
            setCurrentItem(activeItem);
        }
    }, [activeItem]);

    return (
        <ul className={cx("mt-4 flex flex-col", isCollapsed ? "px-2" : "px-2 lg:px-4", className)}>
            {items.map((item, index) => {
                if (item.divider) {
                    return (
                        <li key={index} className="w-full px-0.5 py-2">
                            <hr className="h-px w-full border-none bg-border-secondary" />
                        </li>
                    );
                }

                if (item.items?.length) {
                    if (isCollapsed) {
                        return (
                            <li key={item.label} className="py-0.5">
                                <NavCollapsedMacroPopover item={item} activeUrl={activeUrl} />
                            </li>
                        );
                    }
                    return (
                        <details
                            key={item.label}
                            open={isRouteActive(item.href, activeUrl) || item.items?.some((subItem) => isRouteActive(subItem.href, activeUrl))}
                            className="appearance-none py-0.5"
                            onToggle={(e) => {
                                setOpen(e.currentTarget.open);
                                setCurrentItem(item);
                            }}
                        >
                            <NavItemBase
                                href={item.href}
                                badge={item.badge}
                                icon={item.icon}
                                iconSrc={item.iconSrc}
                                iconHoverSrc={item.iconHoverSrc}
                                type="collapsible"
                            >
                                {item.label}
                            </NavItemBase>

                            <dd>
                                <ul className="py-0.5">
                                    {item.items.map((childItem) => (
                                        <li key={childItem.label} className="py-0.5">
                                            <NavItemBase
                                                href={childItem.href}
                                                badge={childItem.badge}
                                                icon={childItem.icon}
                                                iconSrc={childItem.iconSrc}
                                                iconHoverSrc={childItem.iconHoverSrc}
                                                type="collapsible-child"
                                                current={isRouteActive(childItem.href, activeUrl)}
                                            >
                                                {childItem.label}
                                            </NavItemBase>
                                        </li>
                                    ))}
                                </ul>
                            </dd>
                        </details>
                    );
                }

                return (
                    <li key={item.label} className="py-0.5">
                        <NavItemBase
                            type="link"
                            badge={item.badge}
                            icon={item.icon}
                            iconSrc={item.iconSrc}
                            iconHoverSrc={item.iconHoverSrc}
                            href={item.href}
                            current={isRouteActive(item.href, activeUrl)}
                            open={open && currentItem && item.href ? isRouteActive(currentItem.href, item.href) : false}
                            isCollapsed={isCollapsed}
                        >
                            {!isCollapsed && item.label}
                        </NavItemBase>
                    </li>
                );
            })}
        </ul>
    );
};
